import {
  CameraAltOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  DownloadOutlined,
  EditLocationAltOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  MapOutlined,
  OpenInNewOutlined,
  PushPinOutlined,
  SaveAltOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { amber, grey } from "@mui/material/colors";
import isNil from "lodash/isNil";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import GoogleMapPanel, { buildStaticMapUrl } from "./GoogleMapPanel";
import GoogleMapPanelInteractive from "./GoogleMapPanelInteractive";
import StreetViewPanel, { buildStreetViewUrl } from "./StreetViewPanel";
import { loadGoogleMaps } from "./loadGoogleMaps";
import {
  buildLoanUploadPayloadFromUrl,
  downloadImageUrl,
} from "./mapImageActions";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";

/**
 * PropertyMap — reusable map card with three modes:
 *   - "map"     → Maps Static API, roadmap
 *   - "aerial"  → Maps Static API, satellite, with optional radius circle
 *   - "street"  → Street View Static API
 *
 * Overlay actions (floating icon buttons over the image):
 *   - Open in Google Maps (all modes)
 *   - Save as image        (aerial + street)
 *   - Save image to loan   (aerial + street, only when `property` is provided)
 *
 * Save-to-loan uploads the image with docGroup "Property Documents" and
 * associates it with the given subject property via useLoanFilesHook.
 *
 * Props:
 *   - address    : address object (latitude/longitude/fullAddress)
 *   - property   : the subject-property object — enables "save to loan"
 *   - loanId     : optional override for the association target loan
 *   - docGroup   : defaults to "Property Documents"
 *   - defaultMode: "map" | "aerial" | "street" (default "map")
 *   - height     : container height (default 200px)
 *   - zoom       : roadmap zoom (aerial uses radius-derived zoom instead)
 *   - radiusOptions : aerial radius options in miles (default [1,5,10,20])
 *   - defaultRadius : initial aerial radius in miles (default 1)
 *   - showToggle : set false to hide the mode pill
 *   - compact    : dense layout for tight card contexts
 */
const PropertyMap = ({
  address,
  property,
  loanId: loanIdProp,
  docGroup = "Property Documents",
  defaultMode = "map",
  height = "200px",
  zoom = 14,
  radiusOptions = [1, 5, 10, 20],
  defaultRadius = 1,
  showToggle = true,
  compact = false,
  expandable = false,
  expandedHeight = "480px",
  defaultExpanded = false,
  onExpandChange,
  // "tiny" | "small" | "mid" | "normal" — controls the Maps Static API
  // marker glyph size. Useful when the property card map is small and the
  // default ~30px pin overlaps the radius / address tag.
  pinSize = "normal",
  // Fired when the user drops a pin in aerial mode while pin-drop mode is
  // active. Receives `{ latitude, longitude }`. Used by manual-entry
  // properties to refine their ZIP-centroid placement to a precise point.
  // When null, the pin-drop affordance is hidden entirely.
  onPinDrop,
  sx,
}) => {
  // Manual entry detection: address was hand-entered (raw land / new
  // subdivision) and we only have a wide ZIP centroid until the user
  // drops a pin. Drives the default mode (aerial), a wider default
  // radius, and the "approximate location" banner.
  const isManualEntry = Boolean(address?.manualEntry);
  const isZipApprox = isManualEntry && address?.geocodeQuality !== "pin";
  const initialMode = isZipApprox ? "aerial" : defaultMode;
  const initialRadius = isZipApprox
    ? address?.zipRadiusMiles || 5
    : defaultRadius;

  const [mode, setMode] = useState(initialMode);
  const [radius, setRadius] = useState(initialRadius);
  const [savingTo, setSavingTo] = useState(null);
  const [streetSnapshotUrl, setStreetSnapshotUrl] = useState(null);
  const [aerialSnapshotUrl, setAerialSnapshotUrl] = useState(null);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [pinDropMode, setPinDropMode] = useState(false);
  const [pinSaving, setPinSaving] = useState(false);
  const handleToggleExpanded = () => {
    setIsExpanded((v) => {
      const next = !v;
      onExpandChange?.(next);
      return next;
    });
  };
  const [aerialZoomOverride, setAerialZoomOverride] = useState(null);

  // Leaving aerial mode while pin-drop is active would leave the cursor
  // / affordance in an inconsistent state on return — clear it.
  useEffect(() => {
    if (mode !== "aerial" && pinDropMode) setPinDropMode(false);
  }, [mode, pinDropMode]);

  const canPin = isManualEntry && typeof onPinDrop === "function";

  const handleMapPinDrop = async ({ latitude: lat, longitude: lng }) => {
    if (!canPin || pinSaving) return;
    setPinSaving(true);
    try {
      await onPinDrop({ latitude: lat, longitude: lng });
    } finally {
      setPinSaving(false);
      setPinDropMode(false);
    }
  };

  const effectiveHeight = expandable && isExpanded ? expandedHeight : height;

  // Picking a new radius should drop any manual zoom override so the
  // radius-derived auto-zoom applies again.
  useEffect(() => {
    setAerialZoomOverride(null);
  }, [radius]);

  const MIN_AERIAL_ZOOM = 3;
  const MAX_AERIAL_ZOOM = 20;
  // Mirror of GoogleMapPanel's zoomForRadius so we can compute the
  // starting point when the user hits +/- before ever overriding.
  const radiusBaseZoom = useMemo(() => {
    if (!radius || radius <= 0) return 15;
    const lat = address?.latitude ?? 40;
    const cosLat = Math.cos((Math.abs(lat) * Math.PI) / 180) || 1;
    const radiusMeters = radius * 1609.34;
    const twoToZ = (640 * 40075017 * cosLat) / (2 * radiusMeters * 256);
    return Math.max(1, Math.min(21, Math.ceil(Math.log2(twoToZ))));
  }, [radius, address?.latitude]);

  const handleAerialZoomIn = () =>
    setAerialZoomOverride((z) =>
      Math.min(MAX_AERIAL_ZOOM, (z ?? radiusBaseZoom) + 1),
    );
  const handleAerialZoomOut = () =>
    setAerialZoomOverride((z) =>
      Math.max(MIN_AERIAL_ZOOM, (z ?? radiusBaseZoom) - 1),
    );

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { uploadLoanDocsWithAssociations } = useLoanFilesHook();
  const reduxLoanId = useSelector((state) => state.loanDrilldown?._id);
  const loanId = loanIdProp || reduxLoanId;

  const hasCoords = !isNil(address?.latitude) && !isNil(address?.longitude);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const currentImageUrl = useMemo(() => {
    if (!hasCoords) return null;
    if (mode === "street") {
      // Prefer the live panorama snapshot (reflects current drag/zoom);
      // fall back to the initial Static URL until the panorama reports its
      // first position.
      return (
        streetSnapshotUrl ||
        buildStreetViewUrl({
          apiKey,
          latitude: address.latitude,
          longitude: address.longitude,
          fullAddress: address.fullAddress,
        })
      );
    }
    if (mode === "aerial") {
      // Prefer the live JS-map snapshot (matches current pan/zoom); fall
      // back to the default Static URL until the map emits its first view.
      return (
        aerialSnapshotUrl ||
        buildStaticMapUrl({
          apiKey,
          latitude: address.latitude,
          longitude: address.longitude,
          mapType: "hybrid",
          radiusMiles: radius,
          zoom: aerialZoomOverride,
          pinSize,
        })
      );
    }
    return buildStaticMapUrl({
      apiKey,
      latitude: address.latitude,
      longitude: address.longitude,
      mapType: "roadmap",
      zoom,
      pinSize,
    });
  }, [
    apiKey,
    hasCoords,
    mode,
    address,
    radius,
    zoom,
    streetSnapshotUrl,
    aerialSnapshotUrl,
    aerialZoomOverride,
    pinSize,
  ]);

  // Drop the stale snapshots whenever the target address changes so the
  // next panorama / map init re-seeds them for the new coords.
  useEffect(() => {
    setStreetSnapshotUrl(null);
    setAerialSnapshotUrl(null);
  }, [address?.latitude, address?.longitude]);

  // Warm the Maps JS API as soon as the card is on screen. Without this,
  // the first time the user clicks "Street" the StreetViewPanel has to
  // both fetch and parse the JS bundle AND initialize the panorama, and a
  // layout/loading race can land the panel in the "failed to load" state.
  // Subsequent mounts then work because the script is cached. Preloading
  // here makes panel init synchronous every time.
  useEffect(() => {
    loadGoogleMaps().catch(() => {
      // Errors surface in-panel; swallow here so a missing JS API doesn't
      // taint the map/aerial modes.
    });
  }, []);

  const openInGoogleMaps = () => {
    if (!hasCoords) return;
    const q = encodeURIComponent(`${address.latitude},${address.longitude}`);
    const url =
      mode === "street"
        ? `https://www.google.com/maps?q=&layer=c&cbll=${address.latitude},${address.longitude}`
        : `https://www.google.com/maps/search/?api=1&query=${q}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const displayName = useMemo(
    () => buildViewDisplayName({ mode, radius, address }),
    [mode, radius, address],
  );

  const handleDownload = async () => {
    if (!currentImageUrl) return;
    try {
      setSavingTo("download");
      await downloadImageUrl(currentImageUrl, displayName);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download image", { variant: "error" });
    } finally {
      setSavingTo(null);
    }
  };

  const handleSaveToLoan = async () => {
    if (!currentImageUrl || !loanId || !property?._id) return;
    try {
      setSavingTo("loan");
      const files = await buildLoanUploadPayloadFromUrl({
        url: currentImageUrl,
        displayName,
        docGroup,
      });
      await uploadLoanDocsWithAssociations({
        loanId,
        files,
        subjectPropertyIds: [property._id],
        onSuccessMsg: undefined,
        onSuccessFn: (updatedLoan) => {
          dispatch(loanDrilldownSet(updatedLoan));
          enqueueSnackbar("Image saved to loan", { variant: "success" });
          setSavingTo(null);
        },
        onFailFn: () => setSavingTo(null),
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to save image to loan", { variant: "error" });
      setSavingTo(null);
    }
  };

  const handleDownloadAndSaveToLoan = async () => {
    if (!currentImageUrl) return;
    try {
      setSavingTo("both");
      await downloadImageUrl(currentImageUrl, displayName);
      if (loanId && property?._id) {
        const files = await buildLoanUploadPayloadFromUrl({
          url: currentImageUrl,
          displayName,
          docGroup,
        });
        await uploadLoanDocsWithAssociations({
          loanId,
          files,
          subjectPropertyIds: [property._id],
          onSuccessMsg: undefined,
          onSuccessFn: (updatedLoan) => {
            dispatch(loanDrilldownSet(updatedLoan));
            enqueueSnackbar("Image saved to loan", { variant: "success" });
          },
        });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to save screenshot", { variant: "error" });
    } finally {
      setSavingTo(null);
    }
  };

  const showSaveActions = true;
  // const showSaveActions = mode === "street" || mode === "aerial";
  const canSaveToLoan = Boolean(loanId && property?._id);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        border: `1px solid ${grey[200]}`,
        bgcolor: "#f5f5f5",
        ...sx,
      }}
    >
      {!hasCoords ? (
        <Flx center column sx={{ height: effectiveHeight, gap: 1 }}>
          <MapOutlined sx={{ color: grey[500], fontSize: "2rem" }} />
          <Txt sx={{ color: grey[600], fontSize: 12 }}>
            No location available
          </Txt>
        </Flx>
      ) : mode === "street" ? (
        // Interactive panorama — swallow click/mousedown so drags don't
        // bubble to the parent card's onClick (which opens the property
        // sidebar). Map/aerial modes keep bubbling so that card-click
        // still works there.
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          sx={{ width: "100%", height: effectiveHeight }}
        >
          <StreetViewPanel
            address={address}
            height={effectiveHeight}
            onViewChange={setStreetSnapshotUrl}
          />
        </Box>
      ) : mode === "aerial" ? (
        // Interactive JS-API map — drag to pan, scroll to zoom. Swallow
        // click/mousedown so drags don't bubble to the parent card's
        // onClick (which would open the property sidebar).
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          sx={{ width: "100%", height: effectiveHeight }}
        >
          <GoogleMapPanelInteractive
            address={address}
            height={effectiveHeight}
            mapType="hybrid"
            radiusMiles={radius}
            zoom={aerialZoomOverride ?? radiusBaseZoom}
            onViewChange={setAerialSnapshotUrl}
            pinDropMode={pinDropMode}
            onPinDrop={handleMapPinDrop}
          />
        </Box>
      ) : (
        <GoogleMapPanel
          address={address}
          height={effectiveHeight}
          zoom={zoom}
          pinSize={pinSize}
        />
      )}

      {showToggle && hasCoords && (
        <Box
          sx={{
            position: "absolute",
            top: compact ? 6 : 8,
            left: compact ? 6 : 8,
            display: "inline-flex",
            bgcolor: "rgba(255,255,255,0.95)",
            border: `1px solid ${grey[300]}`,
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            p: "2px",
            zIndex: 2,
          }}
        >
          <ToggleChip
            active={mode === "map"}
            compact={compact}
            onClick={() => setMode("map")}
          >
            Map
          </ToggleChip>
          <ToggleChip
            active={mode === "aerial"}
            compact={compact}
            onClick={() => setMode("aerial")}
          >
            Aerial
          </ToggleChip>
          <ToggleChip
            active={mode === "street"}
            compact={compact}
            onClick={() => setMode("street")}
          >
            Street
          </ToggleChip>
        </Box>
      )}

      {/* Manual-entry banner — bottom-right, above the action stack so
          it doesn't fight the mode toggle on the top-left. Three states:
          (1) ZIP-approximation w/ no pin yet — instructs the user to
          drop a pin; (2) pin-drop active — instructs the user to click;
          (3) pinned — passive confirmation. Hidden once the address is
          no longer manualEntry. */}
      {hasCoords && isManualEntry && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: compact ? 36 : 44,
            left: compact ? 6 : 8,
            maxWidth: compact ? 240 : 320,
            background: pinDropMode
              ? "rgba(255, 244, 224, 0.97)"
              : "rgba(255,255,255,0.96)",
            border: `1px solid ${pinDropMode ? amber[400] : amber[200]}`,
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            mt: "3px",
            px: 1,
            py: 0.5,
            zIndex: 2,
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
          }}
        >
          <EditLocationAltOutlined
            sx={{
              fontSize: 11,
              color: amber[800],
              // mt: "1px",
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 10,
                fontWeight: 500,
                color: amber[900],
                lineHeight: 1.3,
                display: "block",
              }}
            >
              {pinDropMode
                ? "Click on the map to set the precise location"
                : address?.geocodeQuality === "pin"
                  ? "Manual entry · pinned location"
                  : "Precise location not identified"}
            </Txt>
            {!pinDropMode && address?.geocodeQuality !== "pin" && (
              <Txt
                sx={{
                  fontSize: 10.5,
                  color: grey[700],
                  lineHeight: 1.35,
                  mt: 0.25,
                  display: "block",
                }}
              >
                {mode === "aerial"
                  ? "Use the pin button to refine the property's exact spot."
                  : "Switch to Aerial and drop a pin to refine."}
              </Txt>
            )}
          </Box>
        </Box>
      )}

      {mode === "aerial" && hasCoords && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            bottom: compact ? 6 : 8,
            left: compact ? 6 : 8,
            display: "inline-flex",
            gap: "6px",
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              bgcolor: "rgba(255,255,255,0.95)",
              border: `1px solid ${grey[300]}`,
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              p: "2px",
            }}
          >
            {radiusOptions.map((r) => (
              <ToggleChip
                key={r}
                active={radius === r}
                compact={compact}
                onClick={() => setRadius(r)}
              >
                {r} mi
              </ToggleChip>
            ))}
          </Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              bgcolor: "rgba(255,255,255,0.95)",
              border: `1px solid ${grey[300]}`,
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              p: "2px",
            }}
          >
            <ActionButton
              label="Zoom out"
              onClick={handleAerialZoomOut}
              compact={compact}
              disabled={
                (aerialZoomOverride ?? radiusBaseZoom) <= MIN_AERIAL_ZOOM
              }
            >
              <ZoomOutOutlined sx={{ fontSize: compact ? 14 : 16 }} />
            </ActionButton>
            <ActionButton
              label="Zoom in"
              onClick={handleAerialZoomIn}
              compact={compact}
              disabled={
                (aerialZoomOverride ?? radiusBaseZoom) >= MAX_AERIAL_ZOOM
              }
            >
              <ZoomInOutlined sx={{ fontSize: compact ? 14 : 16 }} />
            </ActionButton>
          </Box>
        </Box>
      )}

      {hasCoords && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: "absolute",
            top: compact ? 6 : 8,
            right: compact ? 6 : 8,
            display: "inline-flex",
            gap: "2px",
            bgcolor: "rgba(255,255,255,0.95)",
            border: `1px solid ${grey[300]}`,
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            p: "2px",
            zIndex: 2,
          }}
        >
          {showSaveActions && (
            <ScreenshotMenuButton
              compact={compact}
              saving={savingTo !== null}
              canSaveToLoan={canSaveToLoan}
              docGroup={docGroup}
              onDownload={handleDownload}
              onSaveToLoan={handleSaveToLoan}
              onDownloadAndSaveToLoan={handleDownloadAndSaveToLoan}
            />
          )}
          {expandable && (
            <ActionButton
              label={isExpanded ? "Collapse map" : "Expand map"}
              onClick={handleToggleExpanded}
              compact={compact}
            >
              {isExpanded ? (
                <FullscreenExitOutlined sx={{ fontSize: compact ? 14 : 16 }} />
              ) : (
                <FullscreenOutlined sx={{ fontSize: compact ? 14 : 16 }} />
              )}
            </ActionButton>
          )}
          {canPin && mode === "aerial" && (
            <ActionButton
              label={
                pinDropMode
                  ? "Cancel pin-drop"
                  : "Drop pin to refine precise location"
              }
              onClick={() => setPinDropMode((v) => !v)}
              compact={compact}
              loading={pinSaving}
            >
              {pinDropMode ? (
                <CloseOutlined
                  sx={{ fontSize: compact ? 14 : 16, color: amber[800] }}
                />
              ) : (
                <PushPinOutlined
                  sx={{
                    fontSize: compact ? 14 : 16,
                    color:
                      address?.geocodeQuality === "pin"
                        ? grey[600]
                        : amber[800],
                  }}
                />
              )}
            </ActionButton>
          )}
          <ActionButton
            label="Open in Google Maps"
            onClick={openInGoogleMaps}
            compact={compact}
          >
            <OpenInNewOutlined sx={{ fontSize: compact ? 14 : 16 }} />
          </ActionButton>
        </Box>
      )}
    </Box>
  );
};

const ToggleChip = ({ children, active, compact, onClick }) => (
  <Box
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    sx={{
      px: compact ? 0.9 : 1.25,
      py: compact ? 0.3 : 0.5,
      borderRadius: "6px",
      fontSize: compact ? 10.5 : 11.5,
      fontWeight: active ? 700 : 500,
      color: active ? "#147f97" : grey[700],
      bgcolor: active ? "#e3f3f7" : "transparent",
      cursor: "pointer",
      userSelect: "none",
      transition: "all 0.12s ease",
      "&:hover": {
        bgcolor: active ? "#cdeaf1" : grey[100],
      },
    }}
  >
    <Txt>{children}</Txt>
  </Box>
);

const ActionButton = ({
  children,
  label,
  onClick,
  compact,
  loading,
  disabled,
}) => (
  <Tooltip title={label}>
    <IconButton
      size="small"
      disabled={loading || disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      sx={{
        width: compact ? 22 : 26,
        height: compact ? 22 : 26,
        color: grey[700],
        "&:hover": { bgcolor: grey[100] },
      }}
    >
      {loading ? <CircularProgress size={compact ? 12 : 14} /> : children}
    </IconButton>
  </Tooltip>
);

const ScreenshotMenuButton = ({
  compact,
  saving,
  canSaveToLoan,
  docGroup,
  onDownload,
  onSaveToLoan,
  onDownloadAndSaveToLoan,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };
  const runAndClose = (fn) => (e) => {
    e.stopPropagation();
    setAnchorEl(null);
    fn?.();
  };

  return (
    <>
      <Tooltip title="Screenshot…">
        <span>
          <IconButton
            size="small"
            disabled={saving}
            onClick={handleOpen}
            sx={{
              width: compact ? 22 : 26,
              height: compact ? 22 : 26,
              color: grey[700],
              "&:hover": { bgcolor: grey[100] },
            }}
          >
            {saving ? (
              <CircularProgress size={compact ? 12 : 14} />
            ) : (
              <CameraAltOutlined sx={{ fontSize: compact ? 14 : 16 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        // Lift above the drilldown sidebar/detail panes (z-index 13000) so the
        // menu doesn't render behind them when the map is inside a sidebar.
        sx={{ zIndex: 13500 }}
        slotProps={{
          paper: {
            sx: { mt: 0.5, minWidth: 260 },
          },
        }}
      >
        <MenuItem onClick={runAndClose(onDownload)}>
          <ListItemIcon>
            <DownloadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Save screenshot to desktop"
            primaryTypographyProps={{ fontSize: 13 }}
          />
        </MenuItem>
        {canSaveToLoan && (
          <MenuItem onClick={runAndClose(onSaveToLoan)}>
            <ListItemIcon>
              <CloudUploadOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Add screenshot to loan"
              secondary={docGroup}
              primaryTypographyProps={{ fontSize: 13 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        )}
        {canSaveToLoan && (
          <MenuItem onClick={runAndClose(onDownloadAndSaveToLoan)}>
            <ListItemIcon>
              <SaveAltOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Save to desktop and add to loan"
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

/** Human-readable filename: "Street View - 123 Main Street" etc.
 *  Extension + unique id are handled downstream. */
const buildViewDisplayName = ({ mode, radius, address }) => {
  const { streetNumber, streetName, street, fullAddress } = address || {};
  const streetLine =
    streetNumber && streetName
      ? `${streetNumber} ${streetName}`
      : street ||
        (fullAddress ? fullAddress.split(",")[0].trim() : null) ||
        "Property";

  if (mode === "street") return `Street View - ${streetLine}`;
  if (mode === "aerial") return `Aerial View ${radius}mi - ${streetLine}`;
  return `Map - ${streetLine}`;
};

export default PropertyMap;
